import { FirestoreDataConverter } from '@angular/fire/firestore';
import { ImageModel } from './image.model';
import { Overprint } from './commerce-configuration.model';
import { ObjectHelper } from "../helpers/object.helper";
import { FormArray, FormControl, FormGroup } from "@angular/forms";

type ProductionForm = FormGroup<{
  name: FormControl<string>,
  enabled: FormControl<boolean>,
  min: FormControl<number>,
  percentage: FormControl<number>,
  delay: FormControl<number>
}>
export type CommerceProductForm = FormGroup<{
  id: FormControl<string>,
  name: FormControl<string>,
  description: FormControl<string>,
  shortDescription: FormControl<string>,
  detailedDescription: FormControl<string>,
  baitPrice: FormControl<number>,
  variations: FormControl<string[]>,
  overprints: FormControl<Overprint[]>,
  categories: FormControl<string[]>,
  tags: FormControl<string[]>,
  images: FormControl<ImageModel[]>,
  pdf: FormControl<string>,
  pdfName: FormControl<string>,
  pdfWidth: FormControl<number>,
  pdfHeight: FormControl<number>,
  pdfImage: FormControl<string>,
  pdfImageName: FormControl<string>,
  pdfImageWidth: FormControl<number>,
  pdfImageHeight: FormControl<number>,
  overprintAutoPlacement: FormControl<boolean>,
  withProof: FormControl<boolean>,
  fixedDateSelection: FormControl<boolean>,
  deliveryDateSelection: FormControl<boolean>,
  fixedDateDeliveries: FormControl<string[]>,
  production: FormArray<ProductionForm>,
  fixedFile: FormControl<boolean>,
  technicalName: FormControl<string>,
  promoteLabel: FormControl<string>,
  organizationId: FormControl<string>;
  databaseId: FormControl<string>;
  excludedKeys: FormControl<string[]>;
  defaultDatabaseOptionId: FormControl<string>;
  templatesZip: FormControl<string>;
  templatesZipName: FormControl<string>;
}>

/**
 * Model for Commerce Product (Used for Commerce)
 */
export class CommerceProduct {
  id: string;
  name: string;
  description: string;
  shortDescription: string;
  detailedDescription: string;
  baitPrice: number;
  variations: string[];
  overprints: Overprint[];
  categories: string[];
  tags: string[];
  images: ImageModel[];
  pdf: string;
  pdfName: string;
  pdfWidth: number;
  pdfHeight: number;
  pdfImage: string;
  pdfImageName: string;
  pdfImageWidth: number;
  pdfImageHeight: number;
  overprintAutoPlacement: boolean;
  withProof: boolean;
  fixedDateSelection: boolean;
  deliveryDateSelection: boolean;
  fixedDateDeliveries: string[];
  production: [
    { name: 'standard', enabled: boolean, min: number, percentage: number, delay: number },
    { name: 'priority', enabled: boolean, min: number, percentage: number, delay: number },
    { name: 'express', enabled: boolean, min: number, percentage: number, delay: number },
  ];
  defaultVariationId: string;
  fixedFile: boolean;
  technicalName: string;
  promoteLabel: string;
  organizationId: string;
  databaseId: string;
  excludedKeys: string[];
  defaultDatabaseOptionId: string;
  templatesZip: string;
  templatesZipName: string;

  constructor(data?: Partial<CommerceProduct>) {
    this.id = data?.id ?? '';
    this.variations = data?.variations ?? [];
    this.overprints = data?.overprints.map(o => new Overprint(o)) ?? [];
    this.categories = data?.categories ?? [];
    this.name = data?.name ?? '';
    this.description = data?.description ?? '';
    this.shortDescription = data?.shortDescription ?? '';
    this.detailedDescription = data?.detailedDescription ?? '';
    this.baitPrice = data?.baitPrice ?? 0;
    this.tags = data?.tags ?? [];
    this.images = data?.images.map(i => new ImageModel(i)) ?? [];
    this.pdf = data?.pdf ?? '';
    this.pdfName = data?.pdfName ?? '';
    this.pdfWidth = data?.pdfWidth ?? 0;
    this.pdfHeight = data?.pdfHeight ?? 0;
    this.pdfImage = data?.pdfImage ?? '';
    this.pdfImageName = data?.pdfImageName ?? '';
    this.pdfImageWidth = data?.pdfImageWidth ?? 0;
    this.pdfImageHeight = data?.pdfImageHeight ?? 0;
    this.overprintAutoPlacement = data?.overprintAutoPlacement ?? false;
    this.withProof = data?.withProof ?? false;
    this.fixedDateSelection = data?.fixedDateSelection ?? false;
    this.deliveryDateSelection = data?.deliveryDateSelection ?? false;
    this.fixedDateDeliveries = data?.fixedDateDeliveries ?? [];
    this.production = data?.production ?? [{
      name: 'standard',
      enabled: true,
      min: 0,
      percentage: 0,
      delay: 0,
    }, {
      name: 'priority',
      enabled: false,
      min: 0,
      percentage: 0,
      delay: 0,
    }, {
      name: 'express',
      enabled: false,
      min: 0,
      percentage: 0,
      delay: 0,
    }];
    this.defaultVariationId = data?.defaultVariationId ?? '';
    this.fixedFile = data?.fixedFile ?? false;
    this.technicalName = data?.technicalName ?? '';
    this.promoteLabel = data?.promoteLabel ?? '';
    this.organizationId = data?.organizationId ?? '';
    this.databaseId = data?.databaseId ?? '';
    this.excludedKeys = data?.excludedKeys ?? [];
    this.defaultDatabaseOptionId = data?.defaultDatabaseOptionId ?? '';
    this.templatesZip = data?.templatesZip ?? '';
    this.templatesZipName = data?.templatesZipName ?? '';
  }

  /**
   * Converter used to convert a shipment from firebase
   */
  static converter: FirestoreDataConverter<CommerceProduct> = {
    toFirestore: (product: CommerceProduct) => {
      return {
        id: product.id,
        variations: product.variations,
        overprints: ObjectHelper.anonymize(product.overprints),
        categories: product.categories,
        name: product.name,
        description: product.description,
        shortDescription: product.shortDescription,
        detailedDescription: product.detailedDescription,
        baitPrice: product.baitPrice,
        tags: product.tags,
        images: ObjectHelper.anonymize(product.images),
        pdf: product.pdf,
        pdfName: product.pdfName,
        pdfWidth: product.pdfWidth,
        pdfHeight: product.pdfHeight,
        pdfImage: product.pdfImage,
        pdfImageName: product.pdfImageName,
        pdfImageWidth: product.pdfImageWidth,
        pdfImageHeight: product.pdfImageHeight,
        overprintAutoPlacement: product.overprintAutoPlacement,
        withProof: product.withProof,
        fixedDateSelection: product.fixedDateSelection,
        deliveryDateSelection: product.deliveryDateSelection,
        fixedDateDeliveries: product.fixedDateDeliveries,
        production: product.production,
        defaultVariationId: product.defaultVariationId,
        fixedFile: product.fixedFile,
        technicalName: product.technicalName,
        promoteLabel: product.promoteLabel,
        organizationId: product.organizationId,
        databaseId: product.databaseId,
        excludedKeys: product.excludedKeys,
        defaultDatabaseOptionId: product.defaultDatabaseOptionId,
        templatesZip: product.templatesZip,
        templatesZipName: product.templatesZipName,
      };
    },
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return new CommerceProduct({
        ...data,
        id: snapshot.id,
      });
    }
  };
}
